import { Button, toast } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { FormFieldTypeValue } from "@/server/platform/db/schema/form-center";
import { presignUploadSingleFile } from "@/shared/lib/presign-upload-single-file";
import { useFormDetailQuery, useSubmitFormMutation } from "../api/queries";
import { DocumentPortalTour } from "../tour/DocumentPortalTour";
import { formDetailTourSteps } from "../tour/documentPortalTourSteps";
import { useDocumentPortalTour } from "../tour/useDocumentPortalTour";
import { FormTemplate } from "../ui/FormTemplate";

export function FormDetailPage() {
  const { id } = useParams({ from: "/app/documents/forms/$id" });
  const navigate = useNavigate();
  const tour = useDocumentPortalTour("doc-form-detail-tour-completed");
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useFormDetailQuery(id ?? "");
  const submitMutation = useSubmitFormMutation(id ?? "");
  const [notes, setNotes] = useState("");
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  if (isLoading) {
    return (
      <>
        <Header className="print:hidden" />
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫລດ...</p>
        </Main>
      </>
    );
  }

  if (isError || !data) {
    return (
      <>
        <Header />
        <Main>
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() =>
              navigate({
                to: "/app/documents/forms",
                search: { category: "all", search: undefined, page: 1 },
              })
            }
          >
            <ArrowLeft className="mr-2 size-4" />
            ກັບໄປສູນກາງແບບຟອມ
          </Button>
          <p className="text-muted-foreground">ບໍ່ພົບແບບຟອມ ຫຼື ບໍ່ມີສິດເຂົ້າເບິ່ງ</p>
        </Main>
      </>
    );
  }

  const onDownloadPrintPdf = () => {
    window.print();
  };

  const onSubmitForm = async () => {
    const missing = data.fields.filter(
      (f) => f.isRequired && !hasValue(formValues[f.id], f.fieldType),
    );
    if (missing.length > 0) {
      toast.error("ກະລຸນາກອກຂໍ້ມູນທີ່ບັງຄັບໃຫ້ຄົບ");
      return;
    }

    const fieldById = new Map(data.fields.map((f) => [f.id, f]));
    setIsSubmittingForm(true);
    try {
      const valuesPayload: Array<{
        fieldId: string;
        fieldKey: string;
        value?: unknown;
        fileKey?: string;
      }> = [];

      for (const [fieldId, value] of Object.entries(formValues)) {
        const field = fieldById.get(fieldId);
        if (!field) continue;

        if (field.fieldType === "file") {
          if (value instanceof File) {
            const key = await presignUploadSingleFile(value, "uploads/forms");
            valuesPayload.push({
              fieldId,
              fieldKey: field.fieldKey,
              fileKey: key,
            });
          } else if (typeof value === "string" && value.trim().length > 0) {
            valuesPayload.push({
              fieldId,
              fieldKey: field.fieldKey,
              fileKey: value.trim(),
            });
          }
          continue;
        }

        valuesPayload.push({
          fieldId,
          fieldKey: field.fieldKey,
          value,
        });
      }

      await submitMutation.mutateAsync({
        notes: notes.trim() || null,
        values: valuesPayload,
      });
      setFormValues({});
      setNotes("");
      await queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast.success("ສົ່ງແບບຟອມສຳເລັດ");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "ສົ່ງແບບຟອມບໍ່ສຳເລັດ";
      toast.error(msg);
    } finally {
      setIsSubmittingForm(false);
    }
  };

  return (
    <>
      <Header className="print:hidden" />
      <Main>
        <div className="mb-6 flex flex-wrap items-start gap-3 print:hidden">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">{data.name}</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            {data.version && (
              <p className="mt-1 text-muted-foreground text-sm">
                ເວີຊັນ {data.version}
                {data.versionPublishedAt
                  ? ` • ເຜີຍແຜ່ ${data.versionPublishedAt.slice(0, 10)}`
                  : ""}
              </p>
            )}
          </div>
          <div>
            <Button
              variant="default"
              className="mb-4 print:hidden"
              onClick={() =>
                navigate({
                  to: "/app/documents/forms",
                  search: { category: "all", search: undefined, page: 1 },
                })
              }
            >
              <ArrowLeft className="mr-2 size-4" />
              ກັບໄປສູນກາງແບບຟອມ
            </Button>
          </div>
        </div>

        <div data-tourid="doc-form-detail-content">
          <FormTemplate
            data={data}
            formValues={formValues}
            setFormValues={setFormValues}
            notes={notes}
            setNotes={setNotes}
            onDownloadPrintPdf={onDownloadPrintPdf}
            onSubmitForm={onSubmitForm}
            isSubmitting={isSubmittingForm || submitMutation.isPending}
          />
        </div>
        <DocumentPortalTour
          steps={formDetailTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}

function hasValue(v: unknown, fieldType?: FormFieldTypeValue): boolean {
  if (v == null) return false;
  if (fieldType === "file" && v instanceof File) return v.size > 0;
  if (typeof v === "string") return v.trim().length > 0;
  // For required boolean fields, both true/false mean the user answered.
  if (typeof v === "boolean") return true;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}
